import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { ModalCreateEditBase } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import VirtualTableListing from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { DeleteRow } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import CustomSelect from '../../../../../../../../forms/CustomSelect'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { RectangularInput } from '../../../../../../../../forms/Input'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import { getLang } from '../../../../../../../../utils/globals'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveLangs, getVariantsValues } from '../../../../../../../../utils/reducers/getters'
import { ProductVariantCombinationPrepared, ProductVariantCombinationsPrepared } from '../../types'

interface CreateVariantsSecondsStepProps extends CreateVariantsSecondsStepLogicProps {
    onSubmit: OnSubmit
}

export const CreateVariantsSecondsStep: React.FC<CreateVariantsSecondsStepProps> = ({ onSubmit, ...rest }) => {
    const { id } = useParams()
    const { data, changeData, setLang, changeValCurrentLang, lang, deleteRow, beforeSubmit } = useCreateVariantsSecondsStepLogic({ ...rest })
    const { t } = useTwinTranslation()
    const variants = useMemo(() => getVariantsValues(), [])
    const fields: FormRender = [
        {
            cols: 1,
            title: t('modifyTheFieldsAutogeneratedAndSaveIt', 'Modifica los campos autogenerados y guarda'),
            elements: [
                {
                    component: 'InputHidden',
                    name: 'ProductId',
                    value: id
                }
            ]
        }
    ]
    const columns: ColumnTableSchema<ProductVariantCombinationPrepared> = [
        {
            id: 'combination'+lang,
            dataKey: 'combination',
            label: t('combination', 'Combinación'),
            customRender: (parameterValue: any) => <ShowCombination combination={parameterValue} variants={variants || {}} />
        },
        {
            id: 'name' + lang,
            dataKey: 'name',
            label: t('name', 'Nombre'),
            customRender: (parameterValue, allRowData) => {
                return <RectangularInput value={parameterValue[lang] ?? ''} onChange={(value) => changeValCurrentLang(value, allRowData.rowIndex)}></RectangularInput>
            },
        },
        {
            id: 'reference'+lang,
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={parameterValue ?? ''} required={true} onChange={(value) => changeData('reference', value, allRowData.rowIndex)}></RectangularInput>
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={parameterValue ?? ''} onChange={(value) => changeData('code_bar', value, allRowData.rowIndex)}></RectangularInput>
        },
        {
            id: 'delete' + lang,
            dataKey: 'combination',
            label: '',
            width: 50,
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData.rowIndex)} />,
        }
    ]
    return (
        <ModalCreateEditBase url={'/api/app/product/createSubproductCombinations'} onSubmit={onSubmit} translations={{ title: t('autoGeneration', 'Generación automática'), button: t('generate', 'Generar') }} haveButtonPermissions={true} fields={fields} setOpened={() => { }} beforeSubmit={beforeSubmit} >
            <CustomSelect value={lang} items={getActiveLangs()} label={t('lang', 'Idioma')} onChange={setLang}></CustomSelect>
            <div className='flex-auto'>
                <VirtualTableListing tableData={data} columns={columns} name='createVariantsSecondsStep' />
            </div>
        </ModalCreateEditBase>
    )
}

interface CreateVariantsSecondsStepLogicProps {
    combinations: ProductVariantCombinationsPrepared
}

const useCreateVariantsSecondsStepLogic = ({ combinations }: CreateVariantsSecondsStepLogicProps) => {
    const [data, setData] = useState<ProductVariantCombinationsPrepared>(combinations)
    const [lang, setLang] = useState(getLang())

    const changeData = useCallback(<K extends keyof ProductVariantCombinationPrepared>(key: K, value: ProductVariantCombinationPrepared[K], rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            copy[rowIndex][key] = value
            return ([...copy])
        })
    }, [setData])

    const changeValCurrentLang = useCallback((value: string, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            if (!copy[rowIndex].name) {
                copy[rowIndex].name = {}
            }
            copy[rowIndex].name[lang] = value
            return ([...copy])
        })
    }, [setData, lang])

    const deleteRow = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            copy.splice(rowIndex, 1)
            return ([...copy])
        })
    }, [setData])

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        vals.combinations = data as any
        return true
    }, [data])

    return {  beforeSubmit, data, changeData, changeValCurrentLang, lang, setLang, deleteRow }
}

interface ShowCombinationProps {
    combination: ProductVariantCombinationPrepared['combination']
    variants: TwinDictionary
}

const ShowCombination: React.FC<ShowCombinationProps> = ({ combination, variants }) => {
    const renderThis: string[] = []
    for (const element of combination) {
        renderThis.push(variants[element]?.name)
    }
    return (
        <div>
            {renderThis.join(' - ')}
        </div>
    )
}